import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Lock, Pencil, Phone, Plus, Search, Trash2, UserCheck, UserRound } from "lucide-react";
import { type CustomerClass, type CustomerDto, type Paginated } from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { Button, buttonStyles } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { EmptyTableRow, ListTableFrame, LIST_PAGE_BODY } from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAuth } from "@/app/auth";
import { useCan, useCanSeePrivate, useDataRegion, useFollowedOnly, useMenuAccess } from "@/app/access";
import { useCustomers, useDeleteCustomer } from "@/api/hooks";
import { CurrencyAmountList, currencyAmountsText } from "@/components/domain/CurrencyAmountList";
import { formatDate } from "@/domain/datetime";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { ExportButton } from "@/components/domain/ExportButton";
import { useClampListPage, useListQueryState } from "@/lib/listQuery";

/**
 * Ghost icon-button styling for a real <Link>. Mirrors Button(variant="ghost",
 * size="icon-sm") — an <a> is used instead of a <button> so the row action is
 * deep-linkable, keyboard-focusable and cmd/middle-clickable into a new tab.
 * (Button has no `asChild`, and an <a> may not wrap a <button>.)
 */
const iconLinkCls =
  "inline-flex size-7 items-center justify-center rounded-sm text-ink-soft " +
  "transition-colors hover:bg-sunken hover:text-ink " +
  "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-bright";

const CLASS_FILTERS = ["all", "vietnam", "china"] as const;
type ClassFilter = (typeof CLASS_FILTERS)[number];

export function CustomersPage() {
  const { t } = useTranslation();
  const query = useListQueryState();
  const { user } = useAuth();
  const can = useCan();
  const { search } = useLocation();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  // Tài khoản chỉ một khu vực (29/09/2026) chỉ thấy khách của khu vực đó — bộ lọc Việt Nam / Trung Quốc chỉ còn ra danh sách
  // rỗng, nên ẩn đi và bỏ qua tham số cũ trên đường dẫn.
  const region = useDataRegion();
  const classParam = query.searchParams.get("classification");
  const filter: ClassFilter = !region && CLASS_FILTERS.includes(classParam as ClassFilter)
    ? (classParam as ClassFilter)
    : "all";

  // Khách chưa có người theo đơn (29/09/2026): chỉ người thấy mọi khách mới có gì để lọc — tài khoản "chỉ khách mình theo đơn"
  // không bao giờ thấy khách chưa có người theo đơn.
  const followedOnly = useFollowedOnly();
  const unassignedOnly = !followedOnly && query.searchParams.get("followers") === "none";

  // Khách riêng tư (yêu cầu v1.8 mục 2.1): chỉ quản trị viên nhận được khách riêng tư, nên chỉ họ thấy nút lọc. Nút đứng riêng, chồng
  // được lên Việt Nam / Trung Quốc — riêng tư không thay cho công ty của khách.
  const seesPrivate = useCanSeePrivate();
  // Cột điều khoản thanh toán theo ô Xem "Điều khoản & yêu cầu đặt hàng" (mục 8) — máy chủ cũng không trả giá trị khi thiếu ô đó.
  const seesTerms = useMenuAccess("customerTerms")("view");
  const privateOnly = seesPrivate && query.searchParams.get("privacy") === "private";

  const { data, isLoading, isFetching, error, refetch } = useCustomers({
    q: query.q.trim() || undefined,
    classification: filter === "all" ? undefined : (filter as CustomerClass),
    followers: unassignedOnly ? "none" : undefined,
    privacy: privateOnly ? "private" : undefined,
    page: query.page,
    pageSize: query.pageSize,
  });
  useClampListPage(data?.total, query.clampPage);
  const del = useDeleteCustomer();
  const canCreate = user ? can("records:create") : false;
  const canUpdate = user ? can("records:update") : false;
  const canDelete = user ? can("records:delete") : false;
  const hasActions = canUpdate || canDelete;
  const rows = data?.data ?? [];
  const rowOffset = ((data?.page ?? query.page) - 1) * (data?.pageSize ?? query.pageSize);

  return (
    <PageBody className={LIST_PAGE_BODY}>
      <ConfirmDialog
        open={!!pendingDelete}
        title={pendingDelete?.name ?? t("common.delete")}
        description={t("customers.confirmDeleteNamed", { name: pendingDelete?.name ?? "" })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          setDeleteError(null);
          del.mutate(pendingDelete.id, {
            onError: (error) => setDeleteError(getLocalizedError(error, t, "customers.errorDelete")),
            onSettled: () => setPendingDelete(null),
          });
        }}
      />
      {deleteError && <p role="alert" className="mb-3 rounded-sm bg-danger-tint px-3 py-2 text-sm text-danger">{deleteError}</p>}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          {...query.searchInput}
          leading={<Search />}
          placeholder={t("common.search")}
          aria-label={t("common.search")}
          className="w-full max-w-xs"
        />
        {region ? null : <div className="flex items-center rounded-sm border border-line bg-surface p-0.5">
          {CLASS_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => query.updateParam("classification", f === "all" ? null : f)}
              className={
                "rounded-[3px] px-2.5 py-1 text-xs font-medium transition-colors " +
                (filter === f
                  ? "bg-brand text-brand-on"
                  : "text-ink-faint hover:text-ink-soft")
              }
            >
              {f === "all"
                ? t("common.all")
                : f === "vietnam"
                  ? t("customers.vietnam")
                  : t("customers.china")}
            </button>
          ))}
        </div>}
        {seesPrivate ? (
          <button
            type="button"
            aria-pressed={privateOnly}
            onClick={() => query.updateParam("privacy", privateOnly ? null : "private")}
            title={t("customers.filterPrivateHint")}
            className={
              "inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-xs font-medium transition-colors " +
              (privateOnly ? "border-brand bg-brand text-brand-on" : "border-line bg-surface text-ink-soft hover:text-ink")
            }
          >
            <Lock className="size-3.5" aria-hidden="true" />
            {t("customers.filterPrivate")}
          </button>
        ) : null}
        {followedOnly ? null : (
          <button
            type="button"
            aria-pressed={unassignedOnly}
            onClick={() => query.updateParam("followers", unassignedOnly ? null : "none")}
            className={
              "inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-xs font-medium transition-colors " +
              (unassignedOnly ? "border-brand bg-brand text-brand-on" : "border-line bg-surface text-ink-soft hover:text-ink")
            }
          >
            <AlertTriangle className="size-3.5" aria-hidden="true" />
            {t("customers.filterNoFollower")}
          </button>
        )}
        <div className="ml-auto">
          <ExportButton<CustomerDto>
            name="customers"
            title={t("nav.customers")}
            rows={rows}
            total={data?.total}
            disabled={isFetching}
            errorKey="customers.errorLoad"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<CustomerDto>>("/customers", {
                query: {
                  q: query.q.trim() || undefined,
                  classification: filter === "all" ? undefined : filter,
                  followers: unassignedOnly ? "none" : undefined,
                  privacy: privateOnly ? "private" : undefined,
                  page,
                  pageSize,
                },
                signal,
              })
            }
            columns={[
              { header: t("customers.company"), value: (c) => c.companyName },
              { header: t("customers.code"), value: (c) => c.code },
              {
                header: t("customers.classification"),
                value: (c) => t(c.classification === "china" ? "customers.china" : "customers.vietnam"),
              },
              { header: t("common.address"), value: (c) => c.address },
              { header: t("common.phone"), value: (c) => c.phone },
              // Xem trước người liên hệ đúng như bảng đang hiện: "Tên · điện
              // thoại", ngăn bằng " | ". Danh bạ đầy đủ nằm ở trang chi tiết.
              { header: t("customers.contacts"), value: (c) => c.contactPreview },
              { header: t("customers.contactCount"), value: (c) => c.contactsCount },
              ...(seesTerms ? [{ header: t("customers.terms"), value: (c: CustomerDto) => c.paymentTerm }] : []),
              { header: t("customers.cs"), value: (c) => c.followers.map((f) => f.name).join(", ") },
              { header: t("customers.openAr"), value: (c) => currencyAmountsText(c.openArByCurrency) },
              ...(seesPrivate
                ? [{ header: t("customers.private"), value: (c: CustomerDto) => t(c.isPrivate ? "common.yes" : "common.no") }]
                : []),
              { header: t("pProcess.title"), value: (c) => t(c.pProcessDefault ? "common.yes" : "common.no") },
              { header: t("common.createdOn"), value: (c) => formatDate(c.createdAt) },
              { header: t("common.createdBy"), value: (c) => c.createdByName },
            ]}
          />
        </div>
        {canCreate && (
          <Link to={`/customers/new${search}`} className={buttonStyles({ size: "sm" })}>
            <Plus className="size-4" />
            {t("customers.newCustomer")}
          </Link>
        )}
      </div>

      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("customers.errorLoad")}
        onRetry={() => refetch()}
        page={data?.page ?? query.page}
        pageSize={data?.pageSize ?? query.pageSize}
        total={data?.total ?? 0}
        onPageChange={query.updatePage}
        onPageSizeChange={query.updatePageSize}
      >
            <thead>
              <tr>
                <Th className="w-12" align="right">{t("common.rowNumber")}</Th>
                <Th>{t("customers.company")}</Th>
                <Th>{t("customers.contacts")}</Th>
                {seesTerms && <Th>{t("customers.terms")}</Th>}
                <Th align="right">{t("customers.openAr")}</Th>
                {hasActions && <Th align="right" pinRight>{t("common.actions")}</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <EmptyTableRow colSpan={(hasActions ? 6 : 5) - (seesTerms ? 0 : 1)}>{t("common.noMatches")}</EmptyTableRow>
              )}
              {rows.map((c, index) => (
                <Tr key={c.id}>
                  <Td align="right" className="w-12 font-mono tabular text-xs text-ink-faint">{rowOffset + index + 1}</Td>
                  <Td>
                    {/* Rộng 13rem tới khổ 2xl: bảng vừa khung 1280 px có thanh bên (soát production 30/09/2026 — trước đó cột Công ty
                        18rem từ khổ xl làm cột Điều khoản chui dưới cột Thao tác ghim bên phải). */}
                    <div className="w-52 min-w-0 2xl:w-80 py-2">
                      {/* Nhãn khách riêng tư (30/09/2026) đứng cạnh TÊN, không cạnh nhãn khu vực: không ép cột địa chỉ và không đọc thành
                          một khu vực thứ ba. Chỉ quản trị viên nhận được dòng này. */}
                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                        {/* Mã khách / tên viết tắt (v1.8 mục 2.2) đứng trước tên: nhận diện khách trong nội bộ. */}
                        {c.code ? <span className="shrink-0 rounded-xs bg-sunken px-1 font-mono text-xs font-semibold tabular text-ink">{c.code}</span> : null}
                        <Link to={`/customers/${c.id}${search}`} title={t("customers.openCustomer")}
                          className="min-w-0 whitespace-normal break-words font-medium text-brand hover:underline">{c.companyName}</Link>
                        {c.isPrivate ? (
                          <Badge tone="neutral" title={t("customers.privateHint")}>
                            <Lock className="size-3" aria-hidden="true" /> {t("customers.privateBadge")}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="mt-1 flex items-center gap-1.5">
                        <span className="shrink-0"><Badge tone={c.classification === "china" ? "info" : "neutral"} outline>
                          {t(c.classification === "china" ? "customers.china" : "customers.vietnam")}
                        </Badge></span>
                        <span title={c.address ?? undefined} className="truncate text-xs text-ink-soft">{c.address || "—"}</span>
                      </div>
                      {/* Người theo đơn là một dòng dưới tên khách chứ không phải một cột riêng: thêm cột làm bảng tràn khung ở khổ
                          1280 px (soát production 30/09/2026). Chưa có ai thì chữ cảnh báo kèm biểu tượng, không chỉ màu. */}
                      <p className={`mt-1 flex items-start gap-1.5 text-xs ${c.followers.length ? "text-ink-soft" : "text-warn"}`}>
                        {c.followers.length
                          ? <UserCheck className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                          : <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden="true" />}
                        <span className="min-w-0 whitespace-normal break-words">
                          {t("customers.followersLine", {
                            names: c.followers.length ? c.followers.map((f) => f.name).join(t("customers.followersSeparator")) : t("customers.unassigned"),
                          })}
                        </span>
                      </p>
                    </div>
                  </Td>
                  <Td>
                    <div className="min-w-64 py-2">
                      {c.contactPreview ? (
                        <ul className="space-y-1.5 whitespace-nowrap text-xs text-ink-soft">
                          {c.contactPreview.split(" | ").slice(0, 2).map((contact, i) => {
                            // The list API formats each preview as "name · phone1".
                            const separator = contact.lastIndexOf(" · ");
                            const name = separator < 0 ? contact : contact.slice(0, separator);
                            const phone = separator < 0 ? null : contact.slice(separator + 3);
                            return (
                              <li key={i} className="flex items-center gap-3">
                                <span className="inline-flex items-center gap-1.5" title={t("customers.contactName")}>
                                  <UserRound className="size-3.5 shrink-0 text-ink-soft" aria-hidden="true" />
                                  <span>{name}</span>
                                </span>
                                {phone && <span className="inline-flex items-center gap-1.5" title={t("common.phone")}>
                                  <Phone className="size-3.5 shrink-0 text-ink-soft" aria-hidden="true" />
                                  <span className="font-mono tabular">{phone}</span>
                                </span>}
                              </li>
                            );
                          })}
                        </ul>
                      ) : <span className="text-ink-faint">—</span>}
                      {c.contactsCount > 2 && <p className="mt-1 text-xs text-ink-soft">{t("customers.moreContacts", { count: c.contactsCount - 2 })}</p>}
                    </div>
                  </Td>
                  {seesTerms && <Td>
                    {/* Xuống dòng thay vì một dòng dài: "30% deposit, 70% before shipment" không đẩy bảng tràn khung. */}
                    <div className="w-40 whitespace-normal break-words text-xs text-ink-soft">{c.paymentTerm || "—"}</div>
                  </Td>}
                  <Td align="right">
                    <CurrencyAmountList items={c.openArByCurrency} className="items-end text-xs" />
                  </Td>
                  {hasActions && (
                    <Td align="right" pinRight>
                      <div className="flex items-center justify-end gap-1">
                        {canUpdate && (
                          <Link
                            to={`/customers/${c.id}/edit${search}`}
                            state={{ returnToList: true }}
                            aria-label={t("customers.editNamed", { name: c.companyName })}
                            title={t("common.edit")}
                            className={iconLinkCls}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Pencil className="size-3.5" />
                          </Link>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t("common.delete")}
                            disabled={del.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteError(null);
                              setPendingDelete({ id: c.id, name: c.companyName });
                            }}
                          >
                            <Trash2 className="size-3.5 text-danger" />
                          </Button>
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
